//useFirestore.js
import { db } from "@/firebase/init";
import { doc, getDoc, updateDoc, deleteDoc, setDoc, collection, query, where, getDocs, getCountFromServer, arrayUnion, arrayRemove, limit as firestoreLimit, serverTimestamp, orderBy, limit, startAfter, startAt, endAt, documentId } from "firebase/firestore";
import {usersCollection, globalSettingsCollection, defaultGlobalSettings} from './useGlobalVariables';

/**
 * Fetch user profile from Firestore
 */
export const getUserProfile = async (uid) => {
  console.log(`[Database Fetch] Getting user profile for uid: ${uid}`);
  const userDoc = await getDoc(doc(db, usersCollection, uid));
  console.log(`[Database Fetch] User profile fetch complete: ${userDoc.exists() ? 'found' : 'not found'}`);
  return userDoc.exists() ? userDoc.data() : null;
};

/**
 * Create or update user profile in Firestore
 */
export const updateUserProfile = async (uid, updates) => {
  const userRef = doc(db, usersCollection, uid);
  await updateDoc(userRef, updates);
};

/**
 * Delete user profile from Firestore
 */
export const deleteUserProfile = async (uid) => {
  await deleteDoc(doc(db, usersCollection, uid));
};


/**
 * Fetch global settings from Firestore or create them if missing
 */
export const getGlobalSettings = async () => {
  try {
    console.log('[Database Fetch] Getting global settings');
    const settingsRef = doc(db, globalSettingsCollection, "globalSettings");
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      console.log('[Database Fetch] Global settings found in database');
      return settingsDoc.data();
    } else {
      // If document does not exist, create it with default values
      console.log('[Database Fetch] Global settings not found, creating default settings');
      await setDoc(settingsRef, defaultGlobalSettings);
      console.log("Global settings document created with default values.");
      return defaultGlobalSettings;
    }
  } catch (error) {
    console.error("Error fetching or creating global settings:", error);
    return null;
  }
};

/**
 * Update global settings in Firestore (Admin only)
 */
export const updateGlobalSettings = async (updates, userProfile) => {
  try {
    // Check if user is admin before attempting the update
    if (!userProfile || userProfile.role !== 'admin') {
      return { 
        success: false, 
        message: "Permission denied: Only administrators can update global settings." 
      };
    }
    
    const settingsRef = doc(db, globalSettingsCollection, "globalSettings");
    await updateDoc(settingsRef, updates);
    return { success: true, message: "Global settings updated successfully." };
  } catch (error) {
    console.error("Error updating global settings:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Update only university fields in user profile
 */
export const updateUserUniversity = async (uid, university) => {
  const userRef = doc(db, usersCollection, uid);
  await updateDoc(userRef, { university });
};

/**
 * Update user profile to join socials
 */
export const joinSocials = async (uid) => {
  try {
    const userRef = doc(db, usersCollection, uid);
    await updateDoc(userRef, { joinedSocials: true });
    return { success: true };
  } catch (error) {
    console.error("Error joining socials:", error);
    return { success: false, message: error.message };
  }
};

const toPublicUser = (id, data = {}) => ({
  id,
  name: data.name || data.displayName || 'Student',
  profilePicture: data.profilePicture || '',
  university: data.university || {},
  privacySettings: data.privacySettings || {},
  joinedSocials: data.joinedSocials === true
});

const cleanDiscoveryFilters = (filters = {}) => {
  const clean = {};
  if (filters.universityName) clean['university.name'] = filters.universityName;
  if (filters.faculty) clean['university.faculty'] = filters.faculty;
  if (filters.courseName) clean['university.courseName'] = filters.courseName;
  if (filters.studyYear) clean['university.studyYear'] = filters.studyYear;
  if (filters.levelOfStudy) clean['university.levelOfStudy'] = filters.levelOfStudy;
  return clean;
};

const ordinalSuffix = (year) => {
  if (year === 1) return 'st';
  if (year === 2) return 'nd';
  if (year === 3) return 'rd';
  return 'th';
};

const studyYearQueryVariants = (filterYear) => {
  const selectedYear = Number(filterYear);
  if (!Number.isFinite(selectedYear)) return [filterYear];

  const years = selectedYear >= 5 ? [5, 6, 7] : [selectedYear];
  const variants = [];

  years.forEach((year) => {
    const suffix = ordinalSuffix(year);
    variants.push(
      year,
      String(year),
      `Year ${year}`,
      `${year}${suffix} Year`,
      `${year}${suffix} Year Student`
    );
  });

  return [...new Set(variants)].slice(0, 30);
};

const applyDiscoveryFilters = (constraints, filters = {}, excludedField = '') => {
  const clean = cleanDiscoveryFilters(filters);
  Object.entries(clean).forEach(([field, value]) => {
    if (value && field !== excludedField) {
      constraints.push(
        field === 'university.studyYear'
          ? where(field, 'in', studyYearQueryVariants(value))
          : where(field, '==', value)
      );
    }
  });
  return constraints;
};

const getSearchVariants = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  const upper = trimmed.toUpperCase();
  const title = lower.replace(/\b\w/g, letter => letter.toUpperCase());
  return [...new Set([trimmed, lower, upper, title])];
};

const matchesSearch = (user, searchTerm) => {
  const queryText = searchTerm.trim().toLowerCase();
  if (!queryText) return true;

  const fields = [
    user.name,
    user.university?.faculty,
    user.university?.courseName,
    user.university?.name
  ];

  return fields.some(value => String(value || '').toLowerCase().includes(queryText));
};

const matchesFilters = (user, filters = {}) => {
  if (filters.universityName && user.university?.name !== filters.universityName) return false;
  if (filters.faculty && user.university?.faculty !== filters.faculty) return false;
  if (filters.courseName && user.university?.courseName !== filters.courseName) return false;
  if (filters.studyYear) {
    const userYear = parseInt(user.university?.studyYear, 10);
    const filterYear = parseInt(filters.studyYear, 10);

    if (!Number.isFinite(userYear) || !Number.isFinite(filterYear)) return false;
    if (filterYear >= 5 ? userYear < 5 : userYear !== filterYear) return false;
  }
  if (filters.levelOfStudy && user.university?.levelOfStudy !== filters.levelOfStudy) return false;
  return true;
};

/**
 * Get one page of users who have joined the social platform.
 * Uses document id pagination so the query stays stable without loading everyone.
 */
export const getSocialUsersPage = async ({
  limitCount = 24,
  cursorId = null,
  searchQuery = '',
  filters = {}
} = {}) => {
  try {
    console.log('[Database Fetch] Getting social users page');
    const usersRef = collection(db, usersCollection);
    const trimmedSearch = searchQuery.trim();

    if (trimmedSearch.length >= 2) {
      const searchFields = ['name', 'university.faculty', 'university.courseName'];
      const usersById = {};
      const variants = getSearchVariants(trimmedSearch);

      for (const searchField of searchFields) {
        for (const variant of variants) {
          const searchConstraints = applyDiscoveryFilters([
            where('joinedSocials', '==', true),
            orderBy(searchField),
            startAt(variant),
            endAt(`${variant}\uf8ff`)
          ], filters, searchField);
          searchConstraints.push(firestoreLimit(limitCount));

          try {
            const snapshot = await getDocs(query(usersRef, ...searchConstraints));
            snapshot.forEach((userDoc) => {
              const user = toPublicUser(userDoc.id, userDoc.data());
              if (matchesSearch(user, trimmedSearch) && matchesFilters(user, filters)) {
                usersById[user.id] = user;
              }
            });
          } catch (searchError) {
            console.warn(`Search query failed for ${searchField}; using bounded fallback.`, searchError);
          }
        }
      }

      let users = Object.values(usersById).slice(0, limitCount);

      if (!users.length) {
        const fallbackConstraints = applyDiscoveryFilters([
          where('joinedSocials', '==', true),
          orderBy(documentId()),
          firestoreLimit(Math.max(limitCount * 3, 48))
        ], filters);
        const fallbackSnapshot = await getDocs(query(usersRef, ...fallbackConstraints));
        users = fallbackSnapshot.docs
          .map((userDoc) => toPublicUser(userDoc.id, userDoc.data()))
          .filter((user) => matchesSearch(user, trimmedSearch) && matchesFilters(user, filters))
          .slice(0, limitCount);
      }

      return {
        success: true,
        users,
        cursor: null,
        hasMore: false
      };
    }

    const constraints = [
      where('joinedSocials', '==', true),
      orderBy(documentId())
    ];

    applyDiscoveryFilters(constraints, filters);

    if (cursorId) {
      constraints.push(startAfter(cursorId));
    }

    constraints.push(firestoreLimit(limitCount));

    const socialQuery = query(
      usersRef,
      ...constraints
    );
    
    const querySnapshot = await getDocs(socialQuery);
    const users = querySnapshot.docs.map((userDoc) => toPublicUser(userDoc.id, userDoc.data()));
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    return {
      success: true,
      users,
      cursor: lastDoc?.id || cursorId || null,
      hasMore: querySnapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error("Error getting social users page:", error);
    return { success: false, message: error.message, users: [], cursor: cursorId || null, hasMore: false };
  }
};

const mapAdminUser = (userDoc) => {
  const data = userDoc.data();
  return {
    id: userDoc.id,
    name: data.name || '',
    surname: data.surname || '',
    email: data.email || '',
    role: data.role || 'user',
    profilePicture: data.profilePicture || '',
    registrationDateShort: data.registrationDateShort || '',
    registrationDate: data.registrationDate || null,
    university: data.university || {}
  };
};

const applyAdminFilters = (constraints, filters = {}) => {
  if (filters.role) constraints.push(where('role', '==', filters.role));
  if (filters.faculty) constraints.push(where('university.faculty', '==', filters.faculty));
  if (filters.studyYear) constraints.push(where('university.studyYear', 'in', [Number(filters.studyYear), String(filters.studyYear)]));
  if (filters.campus) constraints.push(where('university.campuses', 'array-contains', filters.campus));
  return constraints;
};

const hasActiveAdminFilters = (filters = {}) => Boolean(
  filters.role || filters.faculty || filters.studyYear || filters.campus
);

const matchesAdminFilters = (user, filters = {}) => {
  if (filters.role && user.role !== filters.role) return false;
  if (filters.faculty && user.university?.faculty !== filters.faculty) return false;
  if (filters.studyYear && Number(user.university?.studyYear) !== Number(filters.studyYear)) return false;
  if (filters.campus && !(user.university?.campuses || []).includes(filters.campus)) return false;
  return true;
};

/**
 * Get one page of registered users for the admin panel, with optional
 * name/email search and role/faculty/studyYear/campus filters.
 * Newest-first when no filter is active; cursor is the raw registrationDate
 * of the last row in that case, or the doc id once a filter is active
 * (equality/array-contains filters need no composite index when ordered by
 * document id instead of registrationDate).
 */
export const getAdminUsersPage = async ({ limitCount = 20, cursor = null, searchQuery = '', filters = {} } = {}) => {
  try {
    console.log('[Database Fetch] Getting admin users page');
    const usersRef = collection(db, usersCollection);
    const trimmedSearch = searchQuery.trim();

    if (trimmedSearch.length >= 2) {
      const searchFields = ['name', 'email'];
      const usersById = {};
      const variants = getSearchVariants(trimmedSearch);
      const lowerSearch = trimmedSearch.toLowerCase();
      const matchesSearchText = (user) => (user.name + ' ' + user.surname + ' ' + user.email).toLowerCase().includes(lowerSearch);

      for (const searchField of searchFields) {
        for (const variant of variants) {
          const searchConstraints = applyAdminFilters([
            orderBy(searchField),
            startAt(variant),
            endAt(`${variant}`)
          ], filters);
          searchConstraints.push(firestoreLimit(limitCount));

          try {
            const snapshot = await getDocs(query(usersRef, ...searchConstraints));
            snapshot.forEach((userDoc) => {
              usersById[userDoc.id] = mapAdminUser(userDoc);
            });
          } catch (searchError) {
            console.warn(`Admin search query failed for ${searchField}.`, searchError);
          }
        }
      }

      let users = Object.values(usersById).filter(matchesSearchText);

      if (!users.length) {
        // Composite index likely missing (or filters + search yielded nothing) -
        // fetch a bounded batch matching just the equality/array-contains
        // filters and search client-side instead.
        const fallbackConstraints = applyAdminFilters([], filters);
        fallbackConstraints.push(orderBy(documentId()), firestoreLimit(Math.max(limitCount * 5, 100)));
        const fallbackSnapshot = await getDocs(query(usersRef, ...fallbackConstraints));
        users = fallbackSnapshot.docs.map(mapAdminUser).filter(matchesSearchText);
      }

      return { success: true, users: users.slice(0, limitCount), cursor: null, hasMore: false };
    }

    const filtersActive = hasActiveAdminFilters(filters);
    const constraints = applyAdminFilters([], filters);

    if (filtersActive) {
      constraints.push(orderBy(documentId()));
    } else {
      constraints.push(orderBy('registrationDate', 'desc'));
    }

    if (cursor) {
      constraints.push(startAfter(cursor));
    }

    constraints.push(firestoreLimit(limitCount));

    let querySnapshot;
    try {
      querySnapshot = await getDocs(query(usersRef, ...constraints));
    } catch (queryError) {
      // Combining several equality/array-contains filters with orderBy(documentId())
      // can require a composite index that may not exist yet - fall back to a
      // single-filter query (the first active one) so the page still loads.
      console.warn('Admin users query failed, retrying with a single filter.', queryError);
      const firstFilterOnly = {};
      const firstKey = ['role', 'faculty', 'studyYear', 'campus'].find((key) => filters[key]);
      if (firstKey) firstFilterOnly[firstKey] = filters[firstKey];
      const fallbackConstraints = applyAdminFilters([], firstFilterOnly);
      fallbackConstraints.push(orderBy(documentId()));
      if (cursor) fallbackConstraints.push(startAfter(cursor));
      fallbackConstraints.push(firestoreLimit(limitCount));
      querySnapshot = await getDocs(query(usersRef, ...fallbackConstraints));
      const users = querySnapshot.docs.map(mapAdminUser).filter((u) => matchesAdminFilters(u, filters));
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      return {
        success: true,
        users,
        cursor: lastDoc ? lastDoc.id : null,
        hasMore: querySnapshot.docs.length === limitCount
      };
    }

    const users = querySnapshot.docs.map(mapAdminUser);
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

    return {
      success: true,
      users,
      cursor: lastDoc ? (filtersActive ? lastDoc.id : lastDoc.data().registrationDate) : null,
      hasMore: querySnapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error("Error getting admin users page:", error);
    return { success: false, message: error.message, users: [], cursor: null, hasMore: false };
  }
};

/**
 * Faculty/campus/study-year option lists for the admin filter sheet and
 * breakdown stats, matching the exact values `university.faculty` /
 * `university.campuses` are actually stored with:
 *  - faculty comes from the `curricula` collection's faculty keys (that's
 *    the real source UserUpdateUniversity.vue validates/stores against,
 *    not globalSettings.universities.*.faculties, which is a different,
 *    often-stale list only used for display elsewhere).
 *  - campus comes from the live globalSettings document, which IS what
 *    the campus picker actually uses.
 */
const getAdminBreakdownFieldOptions = (liveGlobalSettings, liveCurricula) => {
  const campuses = new Set();
  const universities = liveGlobalSettings?.universities || defaultGlobalSettings.universities || {};
  Object.values(universities).forEach((uni) => {
    (uni.campuses || []).forEach((c) => campuses.add(c));
  });
  const faculties = Object.keys(liveCurricula || {});
  return { faculties, campuses: [...campuses], studyYears: [1, 2, 3, 4, 5, 6, 7] };
};

/**
 * Quick platform stats + faculty/study-year/campus breakdowns for the admin
 * panel. Every number comes from a count() aggregation query, so no user
 * documents are ever downloaded to compute this.
 */
export const getAdminStats = async () => {
  try {
    console.log('[Database Fetch] Getting admin stats');
    const usersRef = collection(db, usersCollection);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const [liveGlobalSettings, liveCurricula] = await Promise.all([getGlobalSettings(), getCurriculaData()]);
    const { faculties, campuses, studyYears } = getAdminBreakdownFieldOptions(liveGlobalSettings, liveCurricula);

    const [totalSnap, adminSnap, newSnap, facultySnaps, studyYearSnaps, campusSnaps] = await Promise.all([
      getCountFromServer(usersRef),
      getCountFromServer(query(usersRef, where('role', '==', 'admin'))),
      getCountFromServer(query(usersRef, where('registrationDate', '>=', sevenDaysAgo))),
      Promise.all(faculties.map((faculty) => getCountFromServer(query(usersRef, where('university.faculty', '==', faculty))))),
      Promise.all(studyYears.map((year) => getCountFromServer(query(usersRef, where('university.studyYear', 'in', [year, String(year)]))))),
      Promise.all(campuses.map((campus) => getCountFromServer(query(usersRef, where('university.campuses', 'array-contains', campus)))))
    ]);

    const totalUsers = totalSnap.data().count;
    const toBreakdown = (labels, snaps, maxItems = null) => {
      const allocatedItems = labels
        .map((label, i) => ({ label: typeof label === 'number' ? `Year ${label}` : label, count: snaps[i].data().count }))
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);

      const allocatedCount = allocatedItems.reduce((sum, item) => sum + item.count, 0);
      const unallocatedCount = Math.max(totalUsers - allocatedCount, 0);
      const reservedUnallocatedSlot = unallocatedCount > 0 ? 1 : 0;
      const visibleAllocatedItems = maxItems
        ? allocatedItems.slice(0, Math.max(maxItems - reservedUnallocatedSlot, 0))
        : allocatedItems;

      if (unallocatedCount > 0 || visibleAllocatedItems.length === 0) {
        visibleAllocatedItems.push({
          label: 'Unallocated',
          count: unallocatedCount,
          isUnallocated: true
        });
      }

      return visibleAllocatedItems;
    };

    return {
      success: true,
      totalUsers,
      totalAdmins: adminSnap.data().count,
      newUsersThisWeek: newSnap.data().count,
      facultyBreakdown: toBreakdown(faculties, facultySnaps, 6),
      studyYearBreakdown: toBreakdown(studyYears, studyYearSnaps),
      campusBreakdown: toBreakdown(campuses, campusSnaps, 6)
    };
  } catch (error) {
    console.error("Error getting admin stats:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Get users who have joined the social platform
 */
export const getSocialUsers = async (limitCount = 50) => {
  try {
    const result = await getSocialUsersPage({ limitCount });
    return {
      success: result.success,
      users: result.users,
      message: result.message
    };
  } catch (error) {
    console.error("Error getting social users:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Send connection request to another user
 */
export const sendConnectionRequest = async (fromUserId, toUserId) => {
  try {
    // Add to sender's pendingConnections
    const fromUserRef = doc(db, usersCollection, fromUserId);
    await updateDoc(fromUserRef, {
      pendingConnections: arrayUnion(toUserId)
    });
    
    // Add to recipient's connectionRequests
    const toUserRef = doc(db, usersCollection, toUserId);
    await updateDoc(toUserRef, {
      connectionRequests: arrayUnion(fromUserId)
    });
    
    return { success: true };
  } catch (error) {
    console.error("Error sending connection request:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Accept connection request from another user
 */
export const acceptConnectionRequest = async (userId, requesterId) => {
  try {
    // Add to user's connections and remove from connectionRequests
    const userRef = doc(db, usersCollection, userId);
    await updateDoc(userRef, {
      connections: arrayUnion(requesterId),
      connectionRequests: arrayRemove(requesterId)
    });
    
    // Add to requester's connections and remove from pendingConnections
    const requesterRef = doc(db, usersCollection, requesterId);
    await updateDoc(requesterRef, {
      connections: arrayUnion(userId),
      pendingConnections: arrayRemove(userId)
    });
    
    return { success: true };
  } catch (error) {
    console.error("Error accepting connection request:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Get user's connection information (connections, pending, requests)
 */
export const getUserConnections = async (uid) => {
  try {
    const userRef = doc(db, usersCollection, uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { 
        success: false, 
        message: "User not found" 
      };
    }
    
    const userData = userDoc.data();
    
    return {
      success: true,
      connections: userData.connections || [],
      pendingConnections: userData.pendingConnections || [],
      connectionRequests: userData.connectionRequests || []
    };
  } catch (error) {
    console.error("Error getting user connections:", error);
    return { 
      success: false, 
      message: error.message,
      connections: [],
      pendingConnections: [],
      connectionRequests: []
    };
  }
};

/**
 * Fetch specific user details including connection status for UI display
 */
export const getUserDetails = async (uid) => {
  try {
    const userRef = doc(db, usersCollection, uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { 
        success: false, 
        message: "User not found" 
      };
    }
    
    const userData = userDoc.data();
    
    return {
      success: true,
      userData: {
        ...userData,
        id: uid
      }
    };
  } catch (error) {
    console.error("Error getting user details:", error);
    return { 
      success: false, 
      message: error.message
    };
  }
};

/**
 * Handle connection request - send, accept, or cancel
 * @param {string} action - 'send', 'accept', 'cancel', 'remove'
 * @param {string} fromUserId - Initiating user ID
 * @param {string} toUserId - Target user ID
 */
export const handleConnection = async (action, fromUserId, toUserId) => {
  try {
    const fromUserRef = doc(db, usersCollection, fromUserId);
    const toUserRef = doc(db, usersCollection, toUserId);
    
    switch (action) {
      case 'send':
        // Add to sender's pendingConnections
        await updateDoc(fromUserRef, {
          pendingConnections: arrayUnion(toUserId)
        });
        
        // Add to recipient's connectionRequests
        await updateDoc(toUserRef, {
          connectionRequests: arrayUnion(fromUserId)
        });
        break;
        
      case 'accept':
        // Add to both users' connections lists
        await updateDoc(fromUserRef, {
          connections: arrayUnion(toUserId),
          connectionRequests: arrayRemove(toUserId)
        });
        
        await updateDoc(toUserRef, {
          connections: arrayUnion(fromUserId),
          pendingConnections: arrayRemove(fromUserId)
        });
        break;
        
      case 'cancel':
      case 'reject':
        // Remove from sender's pendingConnections
        await updateDoc(fromUserRef, {
          pendingConnections: arrayRemove(toUserId)
        });
        
        // Remove from recipient's connectionRequests
        await updateDoc(toUserRef, {
          connectionRequests: arrayRemove(fromUserId)
        });
        break;
        
      case 'remove':
        // Handle removal in two separate steps to avoid permission issues
        await updateDoc(fromUserRef, {
          connections: arrayRemove(toUserId)
        });
        
        try {
          // Try to update the other user's document, but don't fail if it doesn't work
          await updateDoc(toUserRef, {
            connections: arrayRemove(fromUserId)
          });
        } catch (innerError) {
          console.warn("Couldn't update the other user's connections. They may need to refresh:", innerError);
          // Don't throw error here - we've already removed from current user's connections
        }
        break;
        
      default:
        throw new Error(`Invalid connection action: ${action}`);
    }
    
    return { success: true };
  } catch (error) {
    console.error(`Error handling connection (${action}):`, error);
    return { success: false, message: error.message };
  }
};

// Consolidate the existing functions to use the new handleConnection function

export const cancelConnectionRequest = async (fromUserId, toUserId) => {
  return handleConnection('cancel', fromUserId, toUserId);
};

/**
 * Remove connection between users - Handles permissions better by updating one user at a time
 */
export const removeConnection = async (userId, connectionId) => {
  try {
    // First, remove the connection from the current user's document
    const userRef = doc(db, usersCollection, userId);
    await updateDoc(userRef, {
      connections: arrayRemove(connectionId)
    });
    
    try {
      // Then try to remove the connection from the other user's document
      const connectionRef = doc(db, usersCollection, connectionId);
      await updateDoc(connectionRef, {
        connections: arrayRemove(userId)
      });
    } catch (err) {
      console.warn("Couldn't update the other user's connections. They may need to refresh.", err);
      // Don't throw error - the main user's connection was removed, which is sufficient
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error removing connection:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Reject a connection request (similar to cancel but initiated by the recipient)
 */
export const rejectConnectionRequest = async (userId, requesterId) => {
  return handleConnection('reject', userId, requesterId);
};

/**
 * Get user's manually entered classes
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, classes: Array, message: string}>}
 */
export const getUserClasses = async (userId) => {
  try {
    const classesRef = collection(db, `users/${userId}/classes`);
    const classesSnapshot = await getDocs(classesRef);
    
    const classes = [];
    classesSnapshot.forEach((doc) => {
      classes.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      classes
    };
  } catch (error) {
    console.error("Error getting user classes:", error);
    return {
      success: false,
      classes: [],
      message: error.message
    };
  }
};

/**
 * Add a new class to the user's timetable
 * @param {string} userId - The user ID
 * @param {Object} classData - The class data to add
 * @returns {Promise<{success: boolean, id: string, message: string}>}
 */
export async function addUserClass(userId, classData) {
  if (!userId || !classData) {
    console.error("Missing userId or classData in addUserClass", { userId, classData });
    return { success: false, message: 'Missing required data' };
  }

  try {
    // Validate class data has all required fields
    const requiredFields = ['moduleCode', 'day', 'startTime', 'endTime', 'venue', 'type'];
    for (const field of requiredFields) {
      if (!classData[field]) {
        return { 
          success: false, 
          message: `Missing required field: ${field}` 
        };
      }
    }
    
    // Create a clean copy of the class data
    const sanitizedData = {
      moduleCode: classData.moduleCode,
      day: classData.day,
      startTime: classData.startTime,
      endTime: classData.endTime,
      venue: classData.venue,
      type: classData.type
    };
    
    // Only add notes if it exists
    if (classData.notes) {
      sanitizedData.notes = classData.notes;
    }
    
    // Create a document reference for the new class
    const classDocRef = doc(collection(db, `users/${userId}/classes`));
    
    // Add the document
    await setDoc(classDocRef, {
      ...sanitizedData,
      id: classDocRef.id,
      createdAt: serverTimestamp()
    });

    return {
      success: true,
      id: classDocRef.id,
      message: 'Class added successfully'
    };
  } catch (error) {
    console.error('Error adding user class:', error);
    return {
      success: false,
      message: error.message || 'Failed to add class'
    };
  }
}

/**
 * Update an existing class in the user's timetable
 * @param {string} userId - The user ID
 * @param {string} classId - The class document ID to update
 * @param {Object} classData - The updated class data
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateUserClass = async (userId, classId, classData) => {
  try {
    const classRef = doc(db, `users/${userId}/classes`, classId);
    
    // Remove the id from the data to avoid storing it in the document
    const { ...dataToUpdate } = classData;
    
    await updateDoc(classRef, {
      ...dataToUpdate,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Error updating user class:", error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Delete a class from the user's timetable
 * @param {string} userId - The user ID
 * @param {string} classId - The class document ID to delete
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteUserClass = async (userId, classId) => {
  try {
    const classRef = doc(db, `users/${userId}/classes`, classId);
    await deleteDoc(classRef);
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Error deleting user class:", error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Get multiple user profiles by their IDs
 * @param {string[]} userIds - Array of user IDs to fetch
 * @returns {Promise<Object>} - Object containing success flag and user data array
 */
export const getUserDataBatch = async (userIds) => {
  try {
    if (!userIds || !userIds.length) {
      return { success: true, users: [] };
    }
    
    const uniqueIds = [...new Set(userIds.filter(Boolean))];
    const users = [];
    const chunkSize = 10;

    for (let i = 0; i < uniqueIds.length; i += chunkSize) {
      const batchIds = uniqueIds.slice(i, i + chunkSize);
      const batchQuery = query(
        collection(db, usersCollection),
        where(documentId(), 'in', batchIds)
      );
      const querySnapshot = await getDocs(batchQuery);

      querySnapshot.forEach((userDoc) => {
        users.push({
          id: userDoc.id,
          ...userDoc.data()
        });
      });
    }

    const usersById = users.reduce((lookup, user) => {
      lookup[user.id] = user;
      return lookup;
    }, {});

    const orderedUsers = uniqueIds
      .map((userId) => usersById[userId])
      .filter(Boolean);
    
    return { 
      success: true,
      users: orderedUsers
    };
  } catch (error) {
    console.error('Error fetching user data batch:', error);
    return { 
      success: false,
      message: error.message,
      users: [] 
    };
  }
};

/**
 * Fetches curricula data from Firestore
 * @returns {Promise<Object|null>} The curricula data or null if not found
 */
export const getCurriculaData = async () => {
  try {
    console.log('Fetching curricula data from Firestore');
    
    // Get all documents from curricula collection
    const snapshot = await getDocs(collection(db, 'curricula'));
    
    if (snapshot.empty) {
      console.log('No curricula data found');
      return null;
    }
    
    // Combine all curricula documents into a single object
    const curricula = {};
    
    snapshot.forEach(doc => {
      const data = doc.data();
      
      // Extract faculty and career from the document ID
      const [faculty, career] = doc.id.split('_');
      
      // Initialize faculty object if it doesn't exist
      if (!curricula[faculty]) {
        curricula[faculty] = {};
      }
      
      // Add career data to faculty
      curricula[faculty][career] = data.programmes;
    });
    
    console.log(`Fetched curricula data for ${Object.keys(curricula).length} faculties`);
    return curricula;
  } catch (error) {
    console.error('Error fetching curricula data:', error);
    throw error;
  }
};

// Posts Collection
export const getPosts = async (limitCount = 50) => {
  try {
    const postsRef = collection(db, 'posts');
    const q = query(postsRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    
    const posts = [];
    querySnapshot.forEach((doc) => {
      posts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, posts };
  } catch (error) {
    console.error('Error getting posts:', error);
    return { success: false, message: error.message };
  }
};

export const createPost = async (postData) => {
  try {
    const postsRef = collection(db, 'posts');
    const newPostRef = doc(postsRef);
    
    const post = {
      ...postData,
      id: newPostRef.id,
      createdAt: serverTimestamp()
    };
    
    await setDoc(newPostRef, post);
    
    return { success: true, post: { ...post, createdAt: new Date() } };
  } catch (error) {
    console.error('Error creating post:', error);
    return { success: false, message: error.message };
  }
};

export const deletePost = async (postId, userId) => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      return { success: false, message: 'Post not found' };
    }
    
    // Check if user is the author of the post
    if (postDoc.data().authorId !== userId) {
      return { success: false, message: 'You can only delete your own posts' };
    }
    
    await deleteDoc(postRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting post:', error);
    return { success: false, message: error.message };
  }
};

export const toggleLikePost = async (postId, userId, isLiking = true) => {
  try {
    const postRef = doc(db, 'posts', postId);
    
    if (isLiking) {
      await updateDoc(postRef, {
        likes: arrayUnion(userId)
      });
    } else {
      await updateDoc(postRef, {
        likes: arrayRemove(userId)
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error toggling like:', error);
    return { success: false, message: error.message };
  }
};

export const addComment = async (postId, comment) => {
  try {
    const postRef = doc(db, 'posts', postId);
    
    // Create a new comment object with a client-side timestamp instead of serverTimestamp
    const commentToAdd = {
      ...comment,
      createdAt: new Date() // Use client-side Date instead of serverTimestamp
    };
    
    await updateDoc(postRef, {
      comments: arrayUnion(commentToAdd)
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error adding comment:', error);
    return { success: false, message: error.message };
  }
};



